// src/components/purchase/NuevaCompraModal.js
// Modal minimalista para nueva factura de compra

import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { createPurchase, updatePurchase, getTermsTypes, getPurchaseDetail } from '../../services/purchaseApi';
import { computeDueDate } from '../../utils/purchaseUtils';
import { dotoniwoofApi } from '../../api/dotoniwooApi';
import './NuevaCompraModal.css';

const formatterCOP = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
});

const todayYYYYMMDD = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const NuevaCompraModal = ({ show, onHide, onSuccess, purchaseId = null }) => {
  const { user } = useAuth();
  const isEditMode = !!purchaseId;
  const [supplierId, setSupplierId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [date, setDate] = useState(todayYYYYMMDD());
  const [subtotal, setSubtotal] = useState('');
  const [totalDiscount, setTotalDiscount] = useState('');
  const [grandTotal, setGrandTotal] = useState('');
  const [referenceDoc, setReferenceDoc] = useState('');
  const [notes, setNotes] = useState('');
  const [termsTypeId, setTermsTypeId] = useState('');

  const [taxes, setTaxes] = useState([]);
  const [selectedTaxToAdd, setSelectedTaxToAdd] = useState('');

  const [supplierOptions, setSupplierOptions] = useState([]);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [taxOptions, setTaxOptions] = useState([]);
  const [termsOptions, setTermsOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const resetForm = () => {
    setSupplierId('');
    setPaymentMethod('');
    setDate(todayYYYYMMDD());
    setSubtotal('');
    setTotalDiscount('');
    setGrandTotal('');
    setReferenceDoc('');
    setNotes('');
    setTermsTypeId('');
    setTaxes([]);
    setSelectedTaxToAdd('');
    setErrorMsg('');
  };

  useEffect(() => {
    if (show) {
      if (!isEditMode) {
        resetForm();
      }
      const fetchSuppliers = async () => {
        try {
          const { data } = await dotoniwoofApi.get('/supplier.php');
          if (data?.code === 200) setSupplierOptions(data.message || []);
        } catch {
          try {
            const { data } = await dotoniwoofApi.get('/supply.php');
            if (data?.code === 200) setSupplierOptions(data.message || []);
          } catch (_) {}
        }
      };
      const fetchPaymentMethods = async () => {
        try {
          const { data } = await dotoniwoofApi.get('/paymentMethod.php/purchase');
          if (data?.code === 200) setPaymentOptions(data.message || []);
        } catch {
          try {
            const { data } = await dotoniwoofApi.get('/paymentMethod.php');
            if (data?.code === 200) setPaymentOptions(data.message || []);
          } catch (_) {}
        }
      };
      const fetchTaxes = async () => {
        try {
          const { data } = await dotoniwoofApi.get('/tax.php', { params: { scope: 'compra' } });
          if (data?.code === 200 && Array.isArray(data.message)) {
            const list = data.message;
            // Evitar duplicados considerando descripción Y tasa (para discriminar IVA 19% vs IVA 5%)
            const seen = new Set();
            const unique = list.filter((t) => {
              const desc = (t.tax_description || t.description || `impuesto-${t.id_tax}`).toLowerCase().trim();
              const rate = t.tax_rate ?? t.rate ?? '';
              // Incluir la tasa en la clave para diferenciar IVA 19% de IVA 5%
              const key = `${desc}|${rate}`;
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });
            setTaxOptions(unique);
          }
        } catch {
          try {
            const { data } = await dotoniwoofApi.get('/tax.php');
            if (data?.code === 200 && Array.isArray(data.message)) {
              const list = data.message;
              const seen = new Set();
              const unique = list.filter((t) => {
                const desc = (t.tax_description || t.description || `impuesto-${t.id_tax}`).toLowerCase().trim();
                const rate = t.tax_rate ?? t.rate ?? '';
                // Incluir la tasa en la clave para diferenciar IVA 19% de IVA 5%
                const key = `${desc}|${rate}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
              });
              setTaxOptions(unique);
            }
          } catch (_) {}
        }
      };
      const fetchTerms = async () => {
        const res = await getTermsTypes();
        if (res.ok && Array.isArray(res.data)) setTermsOptions(res.data);
      };
      fetchSuppliers();
      fetchPaymentMethods();
      fetchTaxes();
      fetchTerms();
    }
  }, [show, isEditMode]);

  const getTaxLabel = (t) => {
    if (!t) return 'Impuesto';
    const desc = t.tax_description || t.description || `Impuesto`;
    const rawRate = t.tax_rate ?? t.rate;
    const rate = rawRate != null && rawRate !== '' ? parseFloat(rawRate) : null;
    const rateStr = rate != null ? `${rate < 1 ? rate * 100 : rate}%` : '';
    const withholding = t.is_withholding ? ' (Retención)' : '';
    const parts = [desc];
    if (rateStr) parts.push(rateStr);
    if (withholding) parts.push(withholding);
    return parts.join(' · ');
  };

  // Cargar datos de la compra cuando está en modo edición
  useEffect(() => {
    const loadPurchaseData = async () => {
      if (!show || !isEditMode || !purchaseId) return;
      setLoadingData(true);
      setErrorMsg('');
      try {
        const res = await getPurchaseDetail(purchaseId);
        if (res.ok && res.data) {
          const purchase = res.data;
          // Formatear fecha para input date (YYYY-MM-DD)
          const purchaseDate = purchase.purchase_date || '';
          const formattedDate = purchaseDate ? purchaseDate.split('T')[0] : todayYYYYMMDD();
          
          setSupplierId(String(purchase.supplier?.id_supplier || purchase.supplier_id || ''));
          setPaymentMethod(String(purchase.payment_method?.id_payment_method || purchase.payment_method_id || ''));
          setDate(formattedDate);
          setSubtotal(String(purchase.subtotal || ''));
          setTotalDiscount(String(purchase.total_discount || ''));
          setGrandTotal(String(purchase.grand_total || ''));
          setReferenceDoc(purchase.reference_doc || '');
          setNotes(purchase.notes || '');
          setTermsTypeId(String(purchase.terms_type?.id_terms_type || purchase.terms_type_id || ''));

          // Cargar impuestos - usar taxOptions si está disponible, sino usar datos del backend
          if (Array.isArray(purchase.purchase_taxes) && purchase.purchase_taxes.length > 0) {
            const loadedTaxes = purchase.purchase_taxes.map((pt) => {
              // Buscar el impuesto en taxOptions para obtener la descripción completa
              const taxDef = taxOptions.find((t) => String(t.id_tax) === String(pt.id_tax));
              // Si taxOptions está disponible, usar getTaxLabel, sino usar la descripción del backend
              const description = taxDef 
                ? getTaxLabel(taxDef) 
                : (pt.tax_description || pt.description || `Impuesto #${pt.id_tax}`);
              return {
                id_tax: pt.id_tax,
                tax_description: description,
                tax_amount: String(pt.tax_amount || ''),
                is_withholding: !!pt.is_withholding
              };
            });
            setTaxes(loadedTaxes);
          } else {
            setTaxes([]);
          }
        } else {
          setErrorMsg('No se pudo cargar la compra para editar.');
        }
      } catch (error) {
        console.error('Error al cargar compra:', error);
        setErrorMsg('Error al cargar los datos de la compra.');
      } finally {
        setLoadingData(false);
      }
    };
    loadPurchaseData();
  }, [show, isEditMode, purchaseId]);
  
  // Actualizar descripciones de impuestos cuando taxOptions esté disponible
  useEffect(() => {
    if (isEditMode && taxes.length > 0 && taxOptions.length > 0) {
      setTaxes((prevTaxes) =>
        prevTaxes.map((t) => {
          const taxDef = taxOptions.find((opt) => String(opt.id_tax) === String(t.id_tax));
          if (taxDef) {
            return {
              ...t,
              tax_description: getTaxLabel(taxDef)
            };
          }
          return t;
        })
      );
    }
  }, [taxOptions, isEditMode]);

  const addedTaxIds = taxes.map((t) => t.id_tax);
  const availableTaxes = taxOptions.filter((t) => !addedTaxIds.includes(t.id_tax));

  // Calcular total sugerido: Subtotal - Descuento + IVA - Retenciones
  const computedTotal = (() => {
    const sub = parseFloat(subtotal) || 0;
    const disc = parseFloat(totalDiscount) || 0;
    const taxEffect = taxes.reduce((acc, t) => {
      const amt = parseFloat(t.tax_amount) || 0;
      return t.is_withholding ? acc - amt : acc + amt;
    }, 0);
    return sub - disc + taxEffect;
  })();


  const handleAddTax = () => {
    if (!selectedTaxToAdd) return;
    const tax = taxOptions.find((t) => String(t.id_tax) === selectedTaxToAdd);
    if (!tax) return;
    setTaxes((prev) => [
      ...prev,
      {
        id_tax: tax.id_tax,
        tax_description: getTaxLabel(tax),
        tax_amount: '',
        is_withholding: !!tax.is_withholding
      }
    ]);
    setSelectedTaxToAdd('');
  };

  const removeTax = (index) => {
    setTaxes((prev) => prev.filter((_, i) => i !== index));
  };

  const updateTax = (index, field, value) => {
    setTaxes((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const validate = () => {
    if (!user?.uid) return 'Sesión no válida.';
    if (!date) return 'La fecha es obligatoria.';
    if (!paymentMethod) return 'Selecciona un método de pago.';
    if (!supplierId) return 'Selecciona un proveedor.';
    const sub = parseFloat(subtotal);
    const total = parseFloat(grandTotal);
    if (isNaN(sub) || sub < 0) return 'Subtotal debe ser un número positivo.';
    if (isNaN(total) || total <= 0) return 'El total debe ser mayor que 0.';

    const taxWithEmptyValue = taxes.find((t) => {
      const val = parseFloat(t.tax_amount);
      return t.tax_amount === '' || t.tax_amount == null || Number.isNaN(val) || val <= 0;
    });
    if (taxWithEmptyValue) {
      return `El impuesto "${taxWithEmptyValue.tax_description}" debe tener un valor mayor que cero.`;
    }

    const disc = parseFloat(totalDiscount) || 0;
    // IVA suma al total; retenciones restan
    const taxSum = taxes.reduce((acc, t) => {
      const amt = parseFloat(t.tax_amount) || 0;
      return t.is_withholding ? acc - amt : acc + amt;
    }, 0);
    const expected = sub - disc + taxSum;
    if (Math.abs(total - expected) > 0.01) {
      return `Total debe coincidir (Subtotal - Descuento + IVA - Retenciones = ${formatterCOP.format(expected)})`;
    }
    return null;
  };

  const buildPayload = () => {
    const payload = {
      firebase_UID: user.uid,
      date,
      supplierId: Number(supplierId),
      paymentMethod: Number(paymentMethod),
      subtotal: parseFloat(subtotal),
      grand_total: parseFloat(grandTotal)
    };
    const disc = parseFloat(totalDiscount);
    if (!isNaN(disc) && disc > 0) payload.total_discount = disc;
    if (referenceDoc) payload.referenceDoc = referenceDoc;
    if (notes) payload.notes = notes;
    if (termsTypeId) {
      payload.termsType = Number(termsTypeId);
      const term = termsOptions.find((t) => String(t.id_terms_type || t.id) === String(termsTypeId));
      const days = term?.days ?? 0;
      payload.due_date = computeDueDate(date, days);
    }

    const taxPayload = taxes
      .filter((t) => t.id_tax && parseFloat(t.tax_amount))
      .map((t) => ({
        id_tax: Number(t.id_tax),
        tax_base: 0,
        tax_rate: 0,
        tax_amount: parseFloat(t.tax_amount) || 0
      }));
    if (taxPayload.length) payload.taxes = taxPayload;

    return payload;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setErrorMsg('');
    const err = validate();
    if (err) {
      setErrorMsg(err);
      return;
    }
    try {
      setLoading(true);
      const payload = buildPayload();
      let result;
      if (isEditMode) {
        result = await updatePurchase(purchaseId, payload);
        if (result.ok && (result.status === 200 || result.data?.code === 200)) {
          onSuccess?.(purchaseId);
          onHide();
          resetForm();
        } else {
          setErrorMsg(result.data?.message || `Error al actualizar (${result.status})`);
        }
      } else {
        result = await createPurchase(payload);
        if (result.ok && (result.status === 201 || result.data?.code === 201)) {
          onSuccess?.(result.data?.id_purchase);
          onHide();
          resetForm();
        } else {
          setErrorMsg(result.data?.message || `Error al crear (${result.status})`);
        }
      }
    } catch {
      setErrorMsg('Error inesperado al enviar.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onHide();
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      className="nueva-compra-modal"
      backdrop="static"
    >
      <Modal.Header className="nueva-compra-modal-header">
        <Modal.Title>{isEditMode ? `Editar compra #${purchaseId}` : 'Nueva compra'}</Modal.Title>
        <button type="button" className="btn-close btn-close-white" aria-label="Cerrar" onClick={handleClose} />
      </Modal.Header>
      <Modal.Body className="nueva-compra-modal-body">
        {loadingData ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" style={{ color: '#EA9D0E' }} />
          </div>
        ) : (
          <>
            {errorMsg && <div className="nueva-compra-error">{errorMsg}</div>}
            <Form onSubmit={handleSubmit}>
              <div className="nueva-compra-form-container">
                {/* Sección: Información General */}
                <div className="nueva-compra-card nueva-compra-card-info">
                  <div className="nueva-compra-card-header">
                    <h6 className="nueva-compra-card-title">Información General</h6>
                  </div>
                  <div className="nueva-compra-card-body">
                    <div className="nueva-compra-field">
                      <Form.Label>
                        <span className="label-icon">🏢</span>
                        Proveedor
                      </Form.Label>
                      {supplierOptions.length > 0 ? (
                        <Form.Select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} required>
                          <option value="">Seleccionar proveedor</option>
                          {supplierOptions.map((s) => (
                            <option key={s.id_supplier || s.id} value={s.id_supplier || s.id}>
                              {s.name || s.supplier_name}
                            </option>
                          ))}
                        </Form.Select>
                      ) : (
                        <Form.Control
                          type="number"
                          placeholder="ID proveedor"
                          value={supplierId}
                          onChange={(e) => setSupplierId(e.target.value)}
                          required
                        />
                      )}
                    </div>
                    <div className="nueva-compra-row">
                      <div className="nueva-compra-field">
                        <Form.Label>
                          <span className="label-icon">📅</span>
                          Fecha
                        </Form.Label>
                        <Form.Control type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
                      </div>
                      <div className="nueva-compra-field">
                        <Form.Label>
                          <span className="label-icon">💳</span>
                          Método de pago
                        </Form.Label>
                        <Form.Select
                          value={paymentMethod}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          required
                          disabled={paymentOptions.length === 0}
                        >
                          <option value="">
                            {paymentOptions.length === 0 ? 'Cargando métodos...' : 'Seleccionar método'}
                          </option>
                          {paymentOptions.map((p) => (
                            <option key={p.id_payment_method || p.id} value={p.id_payment_method || p.id}>
                              {p.method_name || p.name}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    </div>
                    <div className="nueva-compra-row">
                      <div className="nueva-compra-field">
                        <Form.Label>
                          <span className="label-icon">📄</span>
                          Referencia
                        </Form.Label>
                        <Form.Control placeholder="FAC-001" value={referenceDoc} onChange={(e) => setReferenceDoc(e.target.value)} />
                      </div>
                      <div className="nueva-compra-field">
                        <Form.Label>
                          <span className="label-icon">⏱️</span>
                          Términos
                        </Form.Label>
                        <Form.Select
                          value={termsTypeId}
                          onChange={(e) => setTermsTypeId(e.target.value)}
                          disabled={termsOptions.length === 0}
                        >
                          <option value="">
                            {termsOptions.length === 0 ? 'Cargando...' : 'Seleccionar'}
                          </option>
                          {termsOptions.map((t) => (
                            <option key={t.id_terms_type || t.id} value={t.id_terms_type || t.id}>
                              {t.terms_name || t.name || '-'}
                              {t.days != null ? ` (${t.days} días)` : ''}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    </div>
                    <div className="nueva-compra-field">
                      <Form.Label>
                        <span className="label-icon">📝</span>
                        Notas
                      </Form.Label>
                      <Form.Control as="textarea" rows={3} placeholder="Notas adicionales (opcional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </div>
                  </div>
                </div>

                {/* Sección: Totales y Financiero */}
                <div className="nueva-compra-card nueva-compra-card-totales">
                  <div className="nueva-compra-card-header">
                    <h6 className="nueva-compra-card-title">Totales y Financiero</h6>
                  </div>
                  <div className="nueva-compra-card-body">
                    <div className="nueva-compra-totales-grid">
                      <div className="nueva-compra-field">
                        <Form.Label>
                          <span className="label-icon">💰</span>
                          Subtotal
                        </Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={subtotal}
                          onChange={(e) => setSubtotal(e.target.value)}
                          required
                        />
                      </div>
                      <div className="nueva-compra-field">
                        <Form.Label>
                          <span className="label-icon">🎁</span>
                          Descuento
                        </Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={totalDiscount}
                          onChange={(e) => setTotalDiscount(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="nueva-compra-taxes-section">
                      <div className="nueva-compra-taxes-header">
                        <Form.Label>
                          <span className="label-icon">📊</span>
                          Impuestos
                        </Form.Label>
                      </div>
                      <div className="nueva-compra-add-tax">
                        <Form.Select
                          value={selectedTaxToAdd}
                          onChange={(e) => setSelectedTaxToAdd(e.target.value)}
                          disabled={taxOptions.length === 0}
                          className="nueva-compra-tax-select"
                        >
                          <option value="">
                            {taxOptions.length === 0 ? 'No hay impuestos habilitados' : 'Seleccionar impuesto...'}
                          </option>
                          {availableTaxes.map((t) => (
                            <option key={t.id_tax} value={t.id_tax}>
                              {getTaxLabel(t)}
                            </option>
                          ))}
                        </Form.Select>
                        <Button
                          type="button"
                          variant="outline-primary"
                          className="nueva-compra-btn-add-tax"
                          onClick={handleAddTax}
                          disabled={!selectedTaxToAdd}
                        >
                          <span>+</span> Agregar
                        </Button>
                      </div>
                      {taxes.length > 0 && (
                        <div className="nueva-compra-tax-list">
                          {taxes.map((t, i) => (
                            <div key={`${t.id_tax}-${i}`} className="nueva-compra-tax-item">
                              <div className="nueva-compra-tax-info">
                                <span className="nueva-compra-tax-name">{t.tax_description}</span>
                                {t.is_withholding && <span className="nueva-compra-tax-badge">Retención</span>}
                              </div>
                              <div className="nueva-compra-tax-input-wrapper">
                                <span className="nueva-compra-currency">$</span>
                                <Form.Control
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="0.00"
                                  size="sm"
                                  value={t.tax_amount}
                                  onChange={(e) => updateTax(i, 'tax_amount', e.target.value)}
                                  className="nueva-compra-tax-input-valor"
                                />
                              </div>
                              <Button
                                type="button"
                                variant="link"
                                className="nueva-compra-btn-remove-tax"
                                onClick={() => removeTax(i)}
                                aria-label="Quitar impuesto"
                                title="Quitar impuesto"
                              >
                                ✕
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="nueva-compra-total-section">
                      <div className="nueva-compra-field nueva-compra-total-field">
                        <Form.Label>
                          <span className="label-icon">💵</span>
                          Total a pagar
                        </Form.Label>
                        <div className="nueva-compra-total-wrapper">
                          <span className="nueva-compra-currency-large">$</span>
                          <Form.Control
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={grandTotal}
                            onChange={(e) => setGrandTotal(e.target.value)}
                            required
                            className="nueva-compra-input-total"
                          />
                          {computedTotal > 0 && Math.abs(parseFloat(grandTotal || 0) - computedTotal) > 0.01 && (
                            <Button
                              type="button"
                              variant="outline-success"
                              size="sm"
                              className="nueva-compra-btn-calc"
                              onClick={() => setGrandTotal(computedTotal.toFixed(2))}
                            >
                              Usar calculado
                            </Button>
                          )}
                        </div>
                        <Form.Text className="nueva-compra-total-hint">
                          💡 IVA suma · Retenciones restan
                        </Form.Text>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="nueva-compra-actions">
                <Button 
                  type="button" 
                  variant="outline-secondary" 
                  className="nueva-compra-btn-cancel"
                  onClick={handleClose} 
                  disabled={loading || loadingData}
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="nueva-compra-btn-save" 
                  disabled={loading || loadingData}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {isEditMode ? 'Actualizando...' : 'Guardando...'}
                    </>
                  ) : (
                    <>
                      <span className="btn-icon">{isEditMode ? '✏️' : '💾'}</span>
                      {isEditMode ? 'Actualizar compra' : 'Guardar compra'}
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default NuevaCompraModal;
